import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import type { Organization, Patient } from "fhir/r5";
import { useLocation } from "react-router-dom";
import {
  parseEcommerceListSearch,
  serializeEcommerceListSearch,
  useEcommercePatientsList,
  type UseEcommerceWhatsAppEndpointResult,
} from "src-next/modules/ecommerce";
import EcommercePatientsTable from "../components/EcommercePatientsTable";
import EcommercePatientsListSkeleton from "../components/EcommercePatientsListSkeleton";
import EcommercePatientQuickStats from "../components/EcommercePatientQuickStats";

interface EcommercePatientsListSectionProps {
  organization: Organization;
  onOpenPage: (
    patient: Patient,
    transactionId?: string,
    listSearch?: string,
  ) => void;
  listRevision?: number;
  whatsAppChannel: UseEcommerceWhatsAppEndpointResult;
  onSendWhatsApp: (
    patients: Patient[],
    options?: { onSent?: () => void },
  ) => void;
}

export const EcommercePatientsListSection = memo(
  function EcommercePatientsListSection({
    organization,
    onOpenPage,
    listRevision = 0,
    whatsAppChannel,
    onSendWhatsApp,
  }: EcommercePatientsListSectionProps) {
    const location = useLocation();
    const initialListState = useMemo(
      () => parseEcommerceListSearch(location.search),
      // Hidrata só no mount. Não gravar query a cada clique — no hash router
      // isso remonta a lista e desabilita/reseta os filtros.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    const [page, setPage] = useState(initialListState.page);
    const list = useEcommercePatientsList(organization, listRevision, {
      searchTerm: initialListState.searchTerm,
      opFilter: initialListState.opFilter,
    });

    const handleOpenManage = (patient: Patient, transactionId?: string) => {
      onOpenPage(
        patient,
        transactionId,
        serializeEcommerceListSearch({
          searchTerm: list.searchTerm,
          opFilter: list.opFilter,
          page,
        }),
      );
    };

    const skipNextPaginationResetRef = useRef(true);
    useEffect(() => {
      if (skipNextPaginationResetRef.current) {
        skipNextPaginationResetRef.current = false;
        return;
      }
      setPage(0);
    }, [list.searchTerm, list.opFilter]);

    const whatsAppUnavailable = whatsAppChannel.isLoading;
    const showEmptyPatientsAlert =
      Boolean(list.cnpj) &&
      !list.isInitialLoading &&
      list.hasFetchedOnce &&
      !list.error &&
      list.patients.length === 0;

    if (list.isInitialLoading) {
      return <EcommercePatientsListSkeleton />;
    }

    return (
      <Stack spacing={3}>
        {!list.cnpj && (
          <Alert severity="warning">
            O CNPJ da organização não foi informado.
          </Alert>
        )}

        {!list.isInitialLoading && list.error && list.patients.length === 0 && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={list.fetchPatients}>
                Tentar novamente
              </Button>
            }
          >
            Não foi possível carregar os pacientes com consentimento.
          </Alert>
        )}

        {showEmptyPatientsAlert && (
          <Alert severity="info">
            Nenhum paciente com consentimento encontrado.
          </Alert>
        )}

        {list.patients.length > 0 && (
          <>
            {list.listRefreshing && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Atualizando lista de pacientes…
              </Alert>
            )}

            <EcommercePatientQuickStats
              totalCount={list.transactionRows.length}
              docsPendingCount={list.docsPendingCount}
              docsRejectedCount={list.docsRejectedCount}
              docsToApproveCount={list.docsToApproveCount}
              awaitingReleaseCount={list.awaitingReleaseCount}
              docsReleasedCount={list.docsReleasedCount}
              purchaseCompletedCount={list.purchaseCompletedCount}
              activeFilter={list.opFilter}
              statsReady={list.resourcesPreloaded}
              onFilterChange={list.setOpFilter}
            />

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Stack spacing={2}>
                <TextField
                  size="small"
                  label="Buscar paciente"
                  placeholder="Nome, e-mail ou CPF"
                  value={list.searchTerm}
                  onChange={(event) => list.setSearchTerm(event.target.value)}
                  fullWidth
                  sx={{ maxWidth: { sm: 420 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: list.searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Limpar busca"
                          onClick={() => list.setSearchTerm("")}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />

                {list.activeFilterOption &&
                list.opFilter !== "all" &&
                list.resourcesPreloaded ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    {list.activeFilterOption.description}
                    {!list.dispensesPreloaded
                      ? " Contagem de liberações completa ao abrir os pacientes nesta sessão."
                      : null}
                  </Alert>
                ) : null}
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.25,
                borderRadius: 1,
                borderColor: "divider",
                border: 1,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Checkbox
                    color="primary"
                    checked={
                      list.filteredRows.length > 0 &&
                      list.selectedFilteredCount ===
                        list.filteredRows.length
                    }
                    indeterminate={
                      list.selectedFilteredCount > 0 &&
                      list.selectedFilteredCount <
                        list.filteredRows.length
                    }
                    disabled={list.filteredRows.length === 0}
                    onChange={(event) =>
                      list.handleToggleAll(event.target.checked)
                    }
                    inputProps={{ "aria-label": "Selecionar todos" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Selecionar todos ({list.filteredRows.length}{" "}
                    {list.filteredRows.length === 1
                      ? "transação"
                      : "transações"}
                    )
                    {list.selectedKeys.length > 0
                      ? ` · ${list.selectedKeys.length} selecionado(s)`
                      : ""}
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  color="success"
                  startIcon={<WhatsAppIcon />}
                  disabled={
                    list.selectedKeys.length === 0 || whatsAppUnavailable
                  }
                  onClick={() =>
                    onSendWhatsApp(list.selectedPatients, {
                      onSent: list.clearSelection,
                    })
                  }
                  sx={{
                    alignSelf: { xs: "stretch", sm: "auto" },
                    height: 40,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                  }}
                >
                  Enviar WhatsApp ({list.selectedKeys.length})
                </Button>
              </Stack>
            </Paper>

            {list.filteredRows.length === 0 ? (
              <Alert severity="info">
                Nenhuma transação encontrada para a busca ou filtro.
              </Alert>
            ) : (
              <EcommercePatientsTable
                rows={list.filteredRows}
                selectedKeys={list.selectedKeys}
                getRowKey={list.getRowKey}
                onToggleRow={list.handleTogglePatient}
                page={page}
                onPageChange={setPage}
                showStatusColumn={list.resourcesPreloaded}
                onManage={handleOpenManage}
              />
            )}
          </>
        )}
      </Stack>
    );
  },
);
